<script setup lang="ts">
// PairingBanner — v3.13.58 全局待配对面板 (Syncthing "New Device" 式)。
//
// 设计参照 Syncthing 的 panel-warning: 左侧警示色条 + 标题行 + 每设备一行
// 的紧凑列表。比旧版大卡片横滑布局密度高 ~2 倍, 决策信息一行看全;
// 超过 5 条时列表内部滚动, 不无限挤占工作区。
//
// 交互与旧版一致: 接受直接生效; 拒绝走二次确认 (30 分钟静默);
// 详情弹窗展示完整指纹/地址/倒计时, 供与对方电话核验。
import { computed, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { UserPlus } from 'lucide-vue-next'
import Dialog from '@/components/ui/Dialog.vue'
import PairingDetailsModal from './PairingDetailsModal.vue'
import PairingRequestRow from './PairingRequestRow.vue'
import {
  getPairingPending,
  acceptPairing,
  rejectPairing,
  peerHost,
  type PairingRequest,
  type ClusterMember,
} from '@/api/cluster'
import api from '@/api/client'
import { useToast } from '@/composables/useToast'

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

const pairingPendingQuery = useQuery({
  queryKey: ['cluster', 'pairing-pending'],
  queryFn: () => getPairingPending(),
  refetchInterval: 3000,
  // v3.13.58 fix: vue-query 默认「页面失焦即暂停轮询」— 对方把页面切到后台
  // 就永远收不到配对请求, 刷新(回焦)才触发。配对是即时交互, 必须后台继续轮询。
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: true,
})

const pendingRequests = computed<PairingRequest[]>(() => pairingPendingQuery.data.value?.requests ?? [])

// 接受后哪些查询必须标脏 — 不仅是 ['cluster'] 根键, 还有各细粒度子键。
function invalidateClusterRelated(): void {
  qc.invalidateQueries({ queryKey: ['cluster'] })
  qc.invalidateQueries({ queryKey: ['cluster', 'status'] })
  qc.invalidateQueries({ queryKey: ['cluster', 'pairing-pending'] })
  qc.invalidateQueries({ queryKey: ['cluster', 'nodes'] })
  qc.invalidateQueries({ queryKey: ['cluster', 'allowed-devices'] })
  qc.invalidateQueries({ queryKey: ['cluster', 'device-meta'] })
}

// ---- R12.1 对称补齐: 接受后的入群观察 ----
// 添加方 (ClusterTab.watchPairing) 提交后会轮询成员表, 对端一入群就弹
// "已连接 ✓"并刷集群视图; 接受方此前只有点击瞬间的一次刷新 — 那一刻对端
// 还没入群, 拉到的仍是旧成员表, 之后对端真正接入时既无提示也不再刷新,
// 操作者只能手动 F5 才看到新节点 ("以为我这边有什么问题")。这里补上同一套
// 观察: 接受后轮询 /api/cluster/nodes, 对端一出现即提示并刷全部视图。
// v3.19.29: 前 30 秒加速到 1.5s 一查 (配对是即时交互 — 对端拨号循环
// ≤3s + kickJoin 秒级入群, 慢轮询把"点完接受"到"卡片出现"拉长到 5~9s),
// 之后回落 3s, 上限仍 10 分钟与对端拨号循环同界。
const joinedWatches = new Set<number>()

function normalizeFp(raw?: string): string {
  return (raw ?? '').replace(/[\s-]+/g, '').toUpperCase()
}

function watchPeerJoined(fingerprint: string, nodeId: string): void {
  const want = normalizeFp(fingerprint)
  const started = Date.now()
  let tick = 0
  const timer = window.setInterval(async () => {
    tick++
    // 30 秒后降频: 每 2 个 tick 才发一次请求 (1.5s × 2 = 3s)
    if (Date.now() - started > 30000 && tick % 2 === 0) return
    try {
      const members = await api.get<ClusterMember[]>('/api/cluster/nodes')
      const matched = members.some(
        (n: ClusterMember) =>
          !n.is_local &&
          ((want !== '' && normalizeFp(n.device_id) === want) ||
            (nodeId !== '' && n.id === nodeId)),
      )
      if (matched) {
        window.clearInterval(timer)
        joinedWatches.delete(timer)
        toast.success(t('cluster.invite.connected'))
        invalidateClusterRelated()
      } else if (Date.now() - started > 600000) {
        // 与添加方同界 10 分钟 (对端拨号循环也只跑 10 分钟) — 超时静默放弃。
        window.clearInterval(timer)
        joinedWatches.delete(timer)
      }
    } catch {
      /* 网络抖动继续轮询 */
    }
  }, 1500)
  joinedWatches.add(timer)
}

onUnmounted(() => {
  for (const timer of joinedWatches) window.clearInterval(timer)
  joinedWatches.clear()
})

const acceptPairingMutation = useMutation({
  mutationFn: (fp: string) => acceptPairing(fp),
  onSuccess: (resp, fp) => {
    toast.success(t('cluster.pairing.acceptedToast'))
    invalidateClusterRelated()
    // 对端此刻还没入群 — 挂观察等它真正接入后再提示+刷新 (见 watchPeerJoined)。
    watchPeerJoined(fp, resp?.node_id ?? '')
  },
  onError: (err: Error) => toast.error(err.message, 'AFD Console'),
})

const rejectPairingMutation = useMutation({
  mutationFn: (fp: string) => rejectPairing(fp),
  onSuccess: () => {
    toast.success(t('cluster.pairing.rejectedToast'))
    qc.invalidateQueries({ queryKey: ['cluster', 'pairing-pending'] })
  },
  onError: (err: Error) => toast.error(err.message, 'AFD Console'),
})

// 详情 Modal — 当前查看的请求
const detailsTarget = ref<PairingRequest | null>(null)
const detailsOpen = computed({
  get: () => detailsTarget.value !== null,
  set: (v: boolean) => {
    if (!v) detailsTarget.value = null
  },
})

function acceptFromDetails(req: PairingRequest): void {
  acceptPairingMutation.mutate(req.fingerprint)
}

function rejectFromDetails(req: PairingRequest): void {
  detailsTarget.value = null
  confirmReject.value = req
}

// 拒绝二次确认
const confirmReject = ref<PairingRequest | null>(null)
const confirmRejectOpen = computed({
  get: () => confirmReject.value !== null,
  set: (v: boolean) => {
    if (!v) confirmReject.value = null
  },
})

function doReject(): void {
  const target = confirmReject.value
  if (!target) return
  rejectPairingMutation.mutate(target.fingerprint)
  confirmReject.value = null
}

// fingerprint 派生识别色 (HSL 360 取模) — 与详情弹窗同款, 肉眼区分设备
function fingerprintColor(fp: string): string {
  if (!fp) return 'hsl(220 60% 55%)'
  let h = 0
  for (let i = 0; i < Math.min(fp.length, 16); i++) {
    h = (h * 31 + fp.charCodeAt(i)) & 0xffffffff
  }
  return `hsl(${Math.abs(h) % 360} 60% 55%)`
}

// v3.19.27: 标题回退链 节点名 → 来源 IP → node_id → 指纹短码。
// node_id ("AFD-xxxx") 对人无意义, 只配当末位兜底 — 未知名称时宁可
// 显示来源 IP, 操作者一眼能认出是哪台机器。
function displayName(req: PairingRequest): string {
  if (req.name) return req.name
  const host = peerHost(req.addr)
  if (host) return host
  if (req.node_id) return req.node_id
  return req.fingerprint.slice(0, 7)
}
</script>

<template>
  <!-- 气泡样式: 右上角悬浮玻璃卡 + 指向顶栏的小箭头, 覆盖内容不挤压布局 -->
  <div
    v-if="pendingRequests.length"
    class="absolute right-6 top-20 z-30 w-[460px] max-w-[calc(100vw-2rem)]"
    role="alert"
    aria-live="polite"
  >
    <!-- 指向顶栏的小箭头 (旋转方块, 同玻璃底色) -->
    <div
      class="absolute -top-1.5 right-16 h-3 w-3 rotate-45 border-l border-t bg-[var(--glass)]"
      :style="{ borderColor: 'var(--glass-border)' }"
      aria-hidden="true"
    />

    <div class="glass-panel overflow-hidden rounded-2xl">
      <!-- 标题行 -->
      <div class="flex items-center gap-2 px-4 pb-1.5 pt-3">
        <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-warning/20 text-warning">
          <UserPlus :size="12" />
        </span>
        <p class="text-xs font-bold uppercase tracking-wider text-warning">
          {{ t('cluster.pairing.requestsTitle') }}
        </p>
        <span
          v-if="pendingRequests.length > 1"
          class="rounded-full bg-warning/20 px-1.5 text-xs font-semibold leading-4 text-warning"
        >{{ pendingRequests.length }}</span>
        <p class="ml-auto hidden text-xs text-fg-subtle sm:block">
          {{ t('cluster.pairing.expiryHint') }}
        </p>
      </div>

      <!-- 行列表: 每设备一行, >5 条内部滚动 -->
      <div class="divide-y divide-border/50 pb-1.5" style="max-height: 280px; overflow-y: auto">
        <PairingRequestRow
          v-for="req in pendingRequests"
          :key="req.fingerprint"
          :request="req"
          :color="fingerprintColor(req.fingerprint)"
          :display-name="displayName(req)"
          :accepting="acceptPairingMutation.isPending.value && acceptPairingMutation.variables.value === req.fingerprint"
          :rejecting="rejectPairingMutation.isPending.value && rejectPairingMutation.variables.value === req.fingerprint"
          @accept="acceptPairingMutation.mutate(req.fingerprint)"
          @reject="confirmReject = req"
          @details="detailsTarget = req"
        />
      </div>
    </div>

    <!-- 详情 Modal -->
    <PairingDetailsModal
      v-model="detailsOpen"
      :request="detailsTarget"
      :accepting="acceptPairingMutation.isPending.value"
      :rejecting="rejectPairingMutation.isPending.value"
      @accept="acceptFromDetails"
      @reject="rejectFromDetails"
    />

    <!-- 拒绝二次确认 -->
    <Dialog
      v-model="confirmRejectOpen"
      :title="t('cluster.pairing.confirmRejectTitle')"
      :message="t('cluster.pairing.confirmRejectBody')"
      variant="danger"
      :confirm-text="t('cluster.pairing.reject')"
      :cancel-text="t('common.cancel')"
      :loading="rejectPairingMutation.isPending.value"
      @confirm="doReject"
    />
  </div>
</template>
